import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Copy, Users, DollarSign, TrendingUp, Award,
  Clock, CheckCircle2, Wallet, Loader2, ArrowLeft,
  Share2, BarChart3, ChevronRight, Trophy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { WithdrawDialog } from '../components/affiliate/WithdrawDialog';
import { ReferralTreeView } from '../components/affiliate/ReferralTreeView';

const AffiliateDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [referralTree, setReferralTree] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('paypal');
  const [withdrawDetails, setWithdrawDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAllData = useCallback(async () => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [dashRes, refRes, comRes, witRes, leadRes] = await Promise.all([
        axios.get(`${API}/affiliate/dashboard`, h),
        axios.get(`${API}/affiliate/referrals`, h),
        axios.get(`${API}/affiliate/commissions`, h),
        axios.get(`${API}/affiliate/withdrawals`, h),
        axios.get(`${API}/affiliate/leaderboard`, h),
      ]);
      setDashboard(dashRes.data);
      setReferralTree(refRes.data);
      setCommissions(comRes.data.commissions || []);
      setWithdrawals(witRes.data.withdrawals || []);
      setLeaderboard(leadRes.data.leaderboard || []);
    } catch (e) {
      toast.error('Error al cargar datos de afiliado');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  const copyCode = () => {
    if (dashboard?.referral_code) {
      navigator.clipboard.writeText(dashboard.referral_code);
      toast.success('Codigo copiado!');
    }
  };

  const shareLink = () => {
    const url = dashboard?.referral_link || '';
    if (navigator.share) {
      navigator.share({ title: 'NumeroMaestro', text: 'Registrate con mi codigo de referido!', url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copiado!');
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < (dashboard?.min_withdrawal || 20)) {
      toast.error(`Monto minimo: $${dashboard?.min_withdrawal || 20}`); return;
    }
    if (amount > (dashboard?.balance || 0)) { toast.error('Saldo insuficiente'); return; }
    if (!withdrawDetails.trim()) { toast.error('Ingresa los detalles de pago'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/affiliate/withdraw`,
        { amount, payment_method: withdrawMethod, payment_details: { destination: withdrawDetails.trim() } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Solicitud de retiro creada!');
      setShowWithdraw(false);
      setWithdrawAmount('');
      setWithdrawDetails('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al solicitar retiro');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 size={40} className="text-emerald-400 animate-spin" />
      </div>
    );
  }

  const d = dashboard || {};

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-slate-400"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-3xl font-bold">Panel de Afiliados</h1>
            <p className="text-slate-500">Gana comisiones invitando amigos</p>
          </div>
        </div>

        {/* Referral Code Card */}
        <Card className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-500/20 mb-8" data-testid="referral-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-emerald-400 text-sm font-medium mb-1">Tu Codigo de Referido</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-white tracking-wider" data-testid="referral-code">{d.referral_code || '-'}</span>
                  <Button size="sm" variant="ghost" onClick={copyCode} className="text-emerald-400 hover:bg-emerald-500/20" data-testid="copy-code"><Copy size={18} /></Button>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" onClick={shareLink} data-testid="share-link">
                  <Share2 size={16} className="mr-2" /> Compartir
                </Button>
                <Button onClick={() => setShowWithdraw(true)} className="bg-emerald-600 hover:bg-emerald-500" data-testid="withdraw-btn">
                  <Wallet size={16} className="mr-2" /> Retirar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="affiliate-stats">
          {[
            { label: 'Balance', val: `$${(d.balance || 0).toFixed(2)}`, icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
            { label: 'Total Ganado', val: `$${(d.total_earnings || 0).toFixed(2)}`, icon: TrendingUp, color: 'text-blue-400', bg: 'bg-blue-500/20' },
            { label: 'Referidos Totales', val: d.referral_counts?.total || 0, icon: Users, color: 'text-amber-400', bg: 'bg-amber-500/20' },
            { label: 'Pendiente', val: `$${(d.pending_commissions || 0).toFixed(2)}`, icon: Clock, color: 'text-purple-400', bg: 'bg-purple-500/20' },
          ].map(({ label, val, icon: Icon, color, bg }) => (
            <Card key={label} className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center mb-2`}>
                  <Icon size={20} className={color} />
                </div>
                <p className="text-2xl font-bold text-white">{val}</p>
                <p className="text-slate-500 text-sm">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Level Breakdown */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map(level => {
            const breakdown = d.commission_breakdown?.[`level_${level}`] || {};
            return (
              <Card key={level} className="bg-slate-900 border-slate-800" data-testid={`level-${level}-card`}>
                <CardContent className="p-4 text-center">
                  <Badge className={`mb-2 ${level === 1 ? 'bg-green-500/20 text-green-400' : level === 2 ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                    Nivel {level} - {d.commission_rates?.[`level_${level}`] || 0}%
                  </Badge>
                  <p className="text-lg font-bold text-white">{d.referral_counts?.[`level_${level}`] || 0} referidos</p>
                  <p className="text-slate-500 text-sm">${(breakdown.total || 0).toFixed(2)} ganado</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="tree" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800 p-1">
            <TabsTrigger value="tree" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400" data-testid="tab-tree">
              <Users size={16} className="mr-2" /> Red
            </TabsTrigger>
            <TabsTrigger value="commissions" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400" data-testid="tab-commissions">
              <BarChart3 size={16} className="mr-2" /> Comisiones
            </TabsTrigger>
            <TabsTrigger value="withdrawals" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400" data-testid="tab-withdrawals">
              <Wallet size={16} className="mr-2" /> Retiros
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400" data-testid="tab-leaderboard">
              <Trophy size={16} className="mr-2" /> Top
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tree">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Tu Red de Referidos</CardTitle>
                <CardDescription className="text-slate-500">Visualiza tu arbol de referidos hasta 3 niveles</CardDescription>
              </CardHeader>
              <CardContent>
                {referralTree?.referrals?.length > 0 ? (
                  <ReferralTreeView referrals={referralTree.referrals} />
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Users size={40} className="mx-auto mb-3 opacity-50" />
                    <p>Aun no tienes referidos</p>
                    <p className="text-sm mt-1">Comparte tu codigo para empezar a ganar!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="commissions">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="text-white">Historial de Comisiones</CardTitle></CardHeader>
              <CardContent>
                {commissions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <DollarSign size={40} className="mx-auto mb-3 opacity-50" />
                    <p>Sin comisiones aun</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {commissions.map((c, idx) => (
                      <div key={c.id || `com-${idx}`} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/30">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${c.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                            {c.status === 'paid' ? <CheckCircle2 size={18} /> : <Clock size={18} />}
                          </div>
                          <div>
                            <p className="text-white font-medium">{c.referred_user_name || 'Usuario'}</p>
                            <p className="text-slate-500 text-xs">Nivel {c.level} - {new Date(c.created_at).toLocaleDateString('es-DO')}</p>
                          </div>
                        </div>
                        <span className="text-emerald-400 font-bold">${(c.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="withdrawals">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="text-white">Historial de Retiros</CardTitle></CardHeader>
              <CardContent>
                {withdrawals.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Wallet size={40} className="mx-auto mb-3 opacity-50" />
                    <p>Sin retiros aun</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {withdrawals.map((w, idx) => (
                      <div key={w.id || `wit-${idx}`} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/30">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            w.status === 'approved' ? 'bg-green-500/20 text-green-400'
                            : w.status === 'rejected' ? 'bg-red-500/20 text-red-400'
                            : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            <Wallet size={18} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{w.payment_method?.toUpperCase()}</p>
                            <p className="text-slate-500 text-xs">{new Date(w.created_at).toLocaleDateString('es-DO')}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">${(w.amount || 0).toFixed(2)}</p>
                          <Badge className={
                            w.status === 'approved' ? 'bg-green-500/20 text-green-400'
                            : w.status === 'rejected' ? 'bg-red-500/20 text-red-400'
                            : 'bg-amber-500/20 text-amber-400'
                          }>{w.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leaderboard">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Trophy size={20} className="text-amber-400" />Top Afiliados</CardTitle></CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Award size={40} className="mx-auto mb-3 opacity-50" />
                    <p>Sin datos en el leaderboard</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((a) => (
                      <div key={a.rank} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/30">
                        <div className="flex items-center gap-3">
                          <span className={`text-2xl font-black ${a.rank <= 3 ? 'text-amber-400' : 'text-slate-500'}`}>#{a.rank}</span>
                          <span className="text-white font-medium">{a.name}</span>
                        </div>
                        <span className="text-emerald-400 font-bold">${(a.total_earnings || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <WithdrawDialog
          open={showWithdraw} onOpenChange={setShowWithdraw}
          balance={d.balance} minWithdrawal={d.min_withdrawal}
          amount={withdrawAmount} onAmountChange={setWithdrawAmount}
          method={withdrawMethod} onMethodChange={setWithdrawMethod}
          details={withdrawDetails} onDetailsChange={setWithdrawDetails}
          onSubmit={handleWithdraw} submitting={submitting}
        />
      </div>
    </div>
  );
};

export default AffiliateDashboard;
